<template>
  <page-wrapper ref="pageEl" :title="title">
    <div v-if="!$isCallCenter" class="header">
      <sc-button
        :route="createButton.route"
        class="create-button"
        variant="primary"
        >{{ createButton.text }}</sc-button
      >
    </div>
    <div class="wrapper">
      <div class="row mb-3">
        <div class="col-3">
          <div class="label mb-2">ชื่อ Voucher</div>
          <el-input v-model="voucherName" />
        </div>
      </div>
      <div class="text-end">
        <sc-button variant="primary" @click="handleClickSearch"
          >ค้นหา</sc-button
        >
      </div>
      <div v-if="list.length">
        <p class="mb-2 mt-2">{{ totalCount }}</p>
        <div>
          <voucher-package-list-item
            v-for="item in list"
            ref="listItems"
            :key="item.id"
            :value="item"
            @input="handleOrderInput($event)"
            @removeVoucher="handleRemoveVoucher($event)"
            @updateOrder="handleUpdateOrder($event)"
          ></voucher-package-list-item>
          <div v-if="list.length" class="text-end mt-4">
            <el-pagination
              :total="total"
              :size="10"
              :current-page="queryParams.page"
              background
              layout="prev, pager, next"
              @current-change="handleCurrentChange"
            ></el-pagination>
          </div>
        </div>
      </div>
      <div v-else class="text-center p-5">
        <h3>ไม่มีข้อมูล</h3>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper"
import ScButton from "@/lib/components/ScButton"
import { baanRueJaiClient } from "@/dependencies"
import { Input, Pagination } from "element-ui"
import VoucherPackageListItem from "../components/VoucherPackageListItem"

export default {
  components: {
    PageWrapper,
    VoucherPackageListItem,
    ScButton,
    "el-input": Input,
    "el-pagination": Pagination,
  },
  data() {
    const title = "รายการ Ruejai Club Voucher Package"
    const createButton = {
      text: "เพิิ่ม Voucher",
      route: { name: "RuejaiClubVoucherPackageCreatePage" },
    }
    const list = []
    const total = 0
    const voucherName = null

    return {
      title,
      list,
      createButton,
      voucherName,
      total,
    }
  },
  computed: {
    totalCount() {
      const limit = 10
      const total = this.total.toLocaleString()
      const currentTotalItem = this.list.length
      const isLastPage = limit > currentTotalItem
      const currentPage = this.queryParams.page
      const end = (isLastPage ? total : currentPage * limit).toLocaleString()
      const start = (
        (isLastPage ? limit * currentPage - limit : end - limit) + 1
      ).toLocaleString()

      return `แสดงผล ${start} - ${end} จากทั้งหมด ${total}`
    },
    queryParams() {
      return {
        page: parseInt(this.$route.query.page, 10) || 1,
        name: this.$route.query.name || null,
      }
    },
  },
  watch: {
    "$route.query": async function routeQueryWatcher() {
      await this.setupData()

      this.$refs.pageEl.$el.querySelector(".page-content").scrollTop = 0
    },
  },
  mounted() {
    this.setup()
  },
  methods: {
    setup() {
      this.setupQuery()
      this.setupData()
    },
    setupQuery() {
      this.voucherName = this.$route.query.name
    },
    setupData() {
      this.setupList()
    },
    async setupList() {
      const params = this.queryParams
      /* eslint-disable camelcase -- API response */
      const { total_count, items } = await this.fetchList(params)
      // Sampled here, in the same tick that replaces the list, rather than
      // when a save resolved: the reload can be slow and the user may have
      // moved to another field while it was in flight.
      const focusedItemId = this.focusedOrderInputItemId()
      this.total = total_count
      /* eslint-enable camelcase */

      this.list = items.map((item) => {
        const { id, article } = item

        const {
          id: articleId,
          name,
          order,
          thumbnail_url: thumbnailUrl,
        } = article

        const routeParams = {
          id,
          articleId,
        }

        const route = {
          name: "RuejaiClubVoucherPackageDetailPage",
          params: routeParams,
        }

        return {
          id,
          route,
          articleId,
          name,
          order,
          thumbnailUrl,
        }
      })

      // The list is sorted by order, so the row whose order input holds focus
      // may move. Moving a focused element out of the DOM drops its focus, so
      // hand it back to the same item at its new position. Nothing is focused
      // if the item left this page.
      if (focusedItemId) {
        await this.$nextTick()
        this.findListItem(focusedItemId)?.focusOrderInput()
      }
    },
    async fetchList(params) {
      const response =
        await baanRueJaiClient.admin.ruejaiClub.voucherPackages.list(params)

      return response.data.result
    },
    focusedOrderInputItemId() {
      const listItem = (this.$refs.listItems || []).find((item) =>
        item.orderInputHasFocus(),
      )

      return listItem ? listItem.value.id : null
    },
    findListItem(id) {
      return (this.$refs.listItems || []).find((item) => item.value.id === id)
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page },
      }

      this.$router.replace(route)
    },
    async handleClickSearch() {
      const route = {
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: 1,
          name: this.voucherName,
        },
      }

      this.$router.replace(route)
    },
    async handleRemoveVoucher(params) {
      // eslint-disable-next-line no-alert
      if (!window.confirm("ยืนยันการลบ Voucher Package")) {
        return
      }

      try {
        await baanRueJaiClient.admin.ruejaiClub.voucherPackages.delete(
          params.voucherId,
        )

        this.setup()
      } catch (err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง")
        // eslint-disable-next-line no-console
        console.error(err)
      }
    },
    // The list item is a controlled input: the typed order must be written
    // back here, otherwise the field reverts on every keystroke and its
    // change event (which persists the order) never fires.
    handleOrderInput(updatedItem) {
      this.list = this.list.map((item) =>
        item.id === updatedItem.id ? updatedItem : item,
      )
    },
    async handleUpdateOrder(params) {
      try {
        await baanRueJaiClient.admin.ruejaiClub.voucherPackages.updateOrder(
          params,
        )
        await this.setupList()
      } catch (err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง")
        // eslint-disable-next-line no-console
        console.error(err)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.header {
  display: row;
  flex-flow: row nowrap;
  justify-content: space-between;

  padding: 30px;
  border-bottom: $border;
}

.wrapper {
  padding: ($space / 2) $space;
}
</style>
